import {
  isDesignElementFMEARisk,
  RequirementDesignElement,
  TestCaseDesignElement,
  FeatureFlags,
} from "@design-controls/types";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentProduct } from "../../hooks/useCurrentProduct";
import { useDeleteElementDrawer } from "../../hooks/useDesignElementDrawer";
import { DesignElementVersionSelect } from "../../components/DesignElementVersionSelect/DesignElementVersionSelect";
import { DesignElementCompareSelect } from "../../components/DesignElementCompareSelect/DesignElementCompareSelect";
import { useNavigate } from "react-router";
import {
  QBackLink,
  QBodyLayout,
  QButton,
  QButtonGroup,
  QDateMetadataItem,
  QHeader,
  QHorizontalMetadata,
  QNotFound,
  QOpenPropertiesPanelButton,
  QPageLoader,
  QPropertiesPanel,
  QStack,
  QText,
  QTitle,
  QTogglePropertiesPanelButton,
  QVersionMetadata,
  useFeatureFlag,
} from "@qualio/ui-components";
import { isSoureEditableAndDeletable } from "../../domain/editableSource";
import { DesignReviewOverviewTable } from "../../components/DesignReviewOverviewTable/DesignReviewOverviewTable";
import { RequirementTable } from "../../components/OverviewTable/RequirementTable";
import { TestCaseTable } from "../../components/OverviewTable/TestCaseTable";
import { FMEARiskLevel } from "./components/FMEARiskLevel";
import { GapAnalysis } from "../../components/GapAnalysis/GapAnalysis";
import { sourceDisplayName } from "../../constants/sourceDisplayName";
import { DesignElementStatus } from "../../components/DesignElementStatus/DesignElementStatus";
import { useDesignElementVersion } from "../../hooks/designElement/useDesignElementVersion";
import useHasPermission from "../../hooks/useHasPermission";
import { UserPermissions } from "../../domain/permissions";

export const FMEARiskOverview: React.FC = () => {
  const { t } = useTranslation();
  const { name } = useCurrentProduct();
  const {
    isLoading,
    designElement,
    designElementHistory,
    designElementVersions,
    version,
  } = useDeleteElementDrawer();
  const showLinkedVersions = Boolean(
    useFeatureFlag()[FeatureFlags.VERSION_TRACELINK_UI],
  );
  const versionLabel = useDesignElementVersion(
    designElement?.major,
    designElement?.minor,
  );
  const navigate = useNavigate();
  const permissions = useHasPermission([UserPermissions.EDIT_DESIGN_ELEMENTS]);

  const onEditClick = useCallback(() => {
    navigate("edit");
  }, [navigate]);

  const sourceRequirements = useMemo(() => {
    if (!designElement || !isDesignElementFMEARisk(designElement)) {
      return [];
    }

    return [...(designElement.sourceItems ?? [])].filter((element) =>
      element.type.startsWith("req"),
    ) as RequirementDesignElement[];
  }, [designElement]);

  const sourceTestCases = useMemo(() => {
    if (!designElement || !isDesignElementFMEARisk(designElement)) {
      return [];
    }

    return [...(designElement.sourceItems ?? [])].filter((element) =>
      element.type.startsWith("testCase"),
    ) as TestCaseDesignElement[];
  }, [designElement]);

  const mitigatesRequirements = useMemo(() => {
    if (!designElement || !isDesignElementFMEARisk(designElement)) {
      return [];
    }

    return [...(designElement.mitigationItems ?? [])].filter((element) =>
      element.type.startsWith("req"),
    ) as RequirementDesignElement[];
  }, [designElement]);

  const mitigatesTestCases = useMemo(() => {
    if (!designElement || !isDesignElementFMEARisk(designElement)) {
      return [];
    }

    return [...(designElement.mitigationItems ?? [])].filter((element) =>
      element.type.startsWith("testCase"),
    ) as TestCaseDesignElement[];
  }, [designElement]);

  if (isLoading) {
    return <QPageLoader />;
  }

  if (!designElement || !isDesignElementFMEARisk(designElement)) {
    return <QNotFound />;
  }
  return (
    <QBodyLayout.Default>
      <QHeader>
        <QBackLink href="../risk/fmea" data-cy="back-btn">
          {t("products.backLink", { productName: name })}
        </QBackLink>
        <QTitle>
          <QText as="span" color="gray.500">
            {designElement?.code}
          </QText>{" "}
          {designElement?.title}
        </QTitle>
        <QHorizontalMetadata>
          <DesignElementStatus {...designElement} />
          <QVersionMetadata value={versionLabel} data-cy="fmea-risk-version" />
          <QDateMetadataItem
            label={t("common.metadata.created")}
            value={new Date(designElement?.created ?? 0)}
          />
          <QDateMetadataItem
            label={t("common.metadata.lastModified")}
            value={new Date(designElement?.updated ?? 0)}
          />
        </QHorizontalMetadata>
        <QButtonGroup>
          <DesignElementVersionSelect
            designElement={designElement}
            versions={designElementVersions}
          />
          <DesignElementCompareSelect
            designElement={designElement}
            versions={designElementVersions}
          />
          {isSoureEditableAndDeletable(designElement.source) &&
            permissions[UserPermissions.EDIT_DESIGN_ELEMENTS] &&
            version === "v0" && (
              <QButton
                leftIcon="Edit3"
                variant="outline"
                data-cy="edit"
                onClick={onEditClick}
              >
                {t("common.actions.edit")}
              </QButton>
            )}
          {!isSoureEditableAndDeletable(designElement.source) && (
            <a
              href={designElement.url}
              rel="noopener noreferrer"
              target="_blank"
            >
              <QButton
                data-cy="integration-button"
                variant="outline"
                rightIcon="ExternalLink"
              >
                {sourceDisplayName(designElement.source)}
              </QButton>
            </a>
          )}
          <QOpenPropertiesPanelButton />
        </QButtonGroup>
        <GapAnalysis
          policyIssues={designElement.policyIssues}
          type={designElement.type}
        />
      </QHeader>
      <QStack gap="16px" data-cy="fmea-risk-overview">
        <QText fontSize="xl" fontWeight={600}>
          {t("risks.fmea.overview.initialAssessment")}
        </QText>

        <QStack>
          <QText fontSize="sm" fontWeight={600}>
            {t("risks.fmea.overview.fields.failureMode")}
          </QText>
          <QText fontSize="sm" data-cy="failureMode">
            {designElement.failureMode}
          </QText>
        </QStack>

        <QStack direction="row" columnGap="32px">
          <QStack>
            <QText fontSize="sm" fontWeight={600}>
              {t("risks.fmea.overview.fields.riskScore")}
            </QText>
            <QText fontSize="sm">
              <FMEARiskLevel
                mitigation={designElement.preMitigation}
                data-cy="pre-mitigation-level"
              />
            </QText>
          </QStack>
          <QStack>
            <QText fontSize="sm" fontWeight={600}>
              {t("risks.fmea.overview.fields.severity")}
            </QText>
            <QText fontSize="sm" data-cy="pre-mitigation-severity">
              {designElement.preMitigation?.severity}
            </QText>
          </QStack>
          <QStack>
            <QText fontSize="sm" fontWeight={600}>
              {t("risks.fmea.overview.fields.occurence")}
            </QText>
            <QText fontSize="sm" data-cy="pre-mitigation-occurrence">
              {designElement.preMitigation?.occurrence}
            </QText>
          </QStack>
          <QStack>
            <QText fontSize="sm" fontWeight={600}>
              {t("risks.fmea.overview.fields.detectability")}
            </QText>
            <QText fontSize="sm" data-cy="pre-mitigation-detectability">
              {designElement.preMitigation?.detectability}
            </QText>
          </QStack>
        </QStack>

        <QStack>
          <QText fontSize="sm" fontWeight={600}>
            {t("risks.fmea.overview.fields.effectsOfFailure")}
          </QText>
          <QText fontSize="sm" data-cy="failureModeEffects">
            {designElement.failureModeEffects}
          </QText>
        </QStack>

        <QStack>
          <QText fontSize="sm" fontWeight={600}>
            {t("risks.fmea.overview.fields.causesOfFailure")}
          </QText>
          <QText fontSize="sm" data-cy="failureCauses">
            {designElement.failureCauses}
          </QText>
        </QStack>

        <QStack>
          <QText fontSize="sm" fontWeight={600}>
            {t("risks.fmea.overview.fields.currentControlsEvaluation")}
          </QText>
          <QText fontSize="sm" data-cy="evaluationMethod">
            {designElement.evaluationMethod}
          </QText>
        </QStack>

        {designElementHistory && designElementHistory?.length > 0 && (
          <DesignReviewOverviewTable changeHistory={designElementHistory} />
        )}
        <RequirementTable
          requirements={sourceRequirements}
          showLinkedVersions={showLinkedVersions}
        />
        <TestCaseTable
          testCases={sourceTestCases}
          showLinkedVersions={showLinkedVersions}
        />

        {designElement.riskControl && (
          <>
            <QText fontSize="xl" fontWeight={600} data-cy="risk-control-header">
              {t("risks.fmea.overview.riskControl")}
            </QText>

            <QStack>
              <QText fontSize="sm" fontWeight={600}>
                {t("risks.fmea.overview.fields.description")}
              </QText>
              <QText fontSize="sm" data-cy="mitigation">
                {designElement.riskControl.mitigation}
              </QText>
            </QStack>

            {designElement.postMitigation && (
              <QStack direction="row" columnGap="32px">
                <QStack>
                  <QText fontSize="sm" fontWeight={600}>
                    {t("risks.fmea.overview.fields.riskScore")}
                  </QText>
                  <QText fontSize="sm">
                    <FMEARiskLevel
                      mitigation={designElement.postMitigation}
                      data-cy="post-mitigation-level"
                    />
                  </QText>
                </QStack>
                <QStack>
                  <QText fontSize="sm" fontWeight={600}>
                    {t("risks.fmea.overview.fields.severity")}
                  </QText>
                  <QText fontSize="sm" data-cy="post-mitigation-severity">
                    {designElement.postMitigation?.severity}
                  </QText>
                </QStack>
                <QStack>
                  <QText fontSize="sm" fontWeight={600}>
                    {t("risks.fmea.overview.fields.occurence")}
                  </QText>
                  <QText fontSize="sm" data-cy="post-mitigation-occurrence">
                    {designElement.postMitigation?.occurrence}
                  </QText>
                </QStack>
                <QStack>
                  <QText fontSize="sm" fontWeight={600}>
                    {t("risks.fmea.overview.fields.detectability")}
                  </QText>
                  <QText fontSize="sm" data-cy="post-mitigation-detectability">
                    {designElement.postMitigation?.detectability}
                  </QText>
                </QStack>
              </QStack>
            )}
            <RequirementTable
              requirements={mitigatesRequirements}
              showLinkedVersions={showLinkedVersions}
            />
            <TestCaseTable
              testCases={mitigatesTestCases}
              showLinkedVersions={showLinkedVersions}
            />
          </>
        )}

        <QPropertiesPanel openOnMount>
          <QStack spacing="8px" w="300px">
            <QStack direction="row" justifyContent="space-between">
              <QTitle>{t("risks.fmea.overview.properties.riskProfile")}</QTitle>
              <QTogglePropertiesPanelButton />
            </QStack>
            <QStack>
              <QText fontSize="sm" fontWeight={600}>
                {t("risks.fmea.overview.properties.riskProfile")}
              </QText>
              <QText fontSize="sm" data-cy="risk-profile">
                {t("risks.fmea.overview.properties.riskProfileValue")}
              </QText>
            </QStack>
            {designElement.category && (
              <QStack>
                <QText fontSize="sm" fontWeight={600}>
                  {t("risks.fmea.overview.properties.category")}
                </QText>
                <QText fontSize="sm" data-cy="category">
                  {designElement.category?.label}
                </QText>
              </QStack>
            )}
          </QStack>
        </QPropertiesPanel>
      </QStack>
    </QBodyLayout.Default>
  );
};
